import { Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import Animated, { FadeInDown, FadeOut } from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface OneTimeCalloutProps {
  visible: boolean;
  text: string;
  onDismiss: () => void;
  /** Positioning wrapper style (e.g. absolute anchor offsets). */
  style?: StyleProp<ViewStyle>;
  /** 'up' points at something above the bubble; 'down' below. */
  pointer?: 'up' | 'down';
  pointerAlign?: 'left' | 'center';
}

/**
 * OneTimeCallout — tap-to-dismiss tooltip bubble for first-run hints.
 * Pair with useOneTimeCallout for the once-per-install behavior.
 */
export function OneTimeCallout({
  visible,
  text,
  onDismiss,
  style,
  pointer = 'up',
  pointerAlign = 'center',
}: OneTimeCalloutProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  if (!visible) return null;

  const pointerStyle = [
    styles.pointerBase,
    pointer === 'up'
      ? { borderBottomWidth: 8, borderBottomColor: palette.primary }
      : { borderTopWidth: 8, borderTopColor: palette.primary },
    pointerAlign === 'left' && styles.pointerLeft,
  ];

  return (
    <Animated.View
      entering={FadeInDown.duration(250)}
      exiting={FadeOut.duration(150)}
      style={[styles.wrapper, pointerAlign === 'left' && styles.wrapperLeft, style]}
      pointerEvents="box-none"
    >
      {pointer === 'up' && <View style={pointerStyle} />}
      <Pressable
        onPress={onDismiss}
        style={[styles.bubble, { backgroundColor: palette.primary }]}
        accessibilityRole="button"
        accessibilityLabel={`Dismiss hint: ${text}`}
      >
        <Text style={[styles.bubbleText, { fontFamily: getBodyFont() }]}>{text}</Text>
      </Pressable>
      {pointer === 'down' && <View style={pointerStyle} />}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    alignItems: 'center',
    zIndex: 101,
  },
  wrapperLeft: {
    alignItems: 'flex-start',
  },
  bubble: {
    maxWidth: 300,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 6,
  },
  bubbleText: {
    color: '#FFFFFF',
    fontSize: 14,
    textAlign: 'center',
  },
  pointerBase: {
    width: 0,
    height: 0,
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  pointerLeft: {
    marginLeft: 24,
  },
});
